import React, { useState, useCallback, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import { VanityAPI } from '../services/api';

interface StandardImageUploaderProps {
    isOpen: boolean;
    onClose: () => void;
    onUploadComplete: (imageRecord: any) => void;
    aspectRatio?: number;
    initialFile?: File | null; // NEW PROP
    entityType: string;
    entityId: string;
    role: string;
    modalTitle?: string;
}

const StandardImageUploader: React.FC<StandardImageUploaderProps> = ({
    isOpen,
    onClose,
    onUploadComplete,
    aspectRatio = 16 / 9,
    initialFile = null,
    entityType,
    entityId,
    role,
    modalTitle = "Upload Image"
}) => {
    const [file, setFile] = useState<File | null>(initialFile);
    const [fileUrl, setFileUrl] = useState<string | null>(initialFile ? URL.createObjectURL(initialFile) : null);

    // Effect to update if initialFile changes (optional safety)
    useEffect(() => {
        if (initialFile) {
            setFile(initialFile);
            setFileUrl(URL.createObjectURL(initialFile));
        }
    }, [initialFile]);

    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [aspect, setAspect] = useState(aspectRatio);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const f = e.target.files[0];
            setFile(f);
            setFileUrl(URL.createObjectURL(f));
            setError(null);
        }
    };

    const onCropComplete = useCallback((croppedArea: any, croppedAreaPixels: any) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleUpload = async () => {
        if (!file || !croppedAreaPixels) return;
        setUploading(true);
        setError(null);

        try {
            const cropJson = {
                unit: 'px',
                x: croppedAreaPixels.x,
                y: croppedAreaPixels.y,
                width: croppedAreaPixels.width,
                height: croppedAreaPixels.height
            };

            const imageRecord = await VanityAPI.uploadStandardImage(
                file,
                entityType,
                entityId,
                role,
                cropJson
            );

            onUploadComplete(imageRecord);
            handleClose();
        } catch (err: any) {
            console.error("Standard Upload Error:", err);
            setError(err.message || 'Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => {
        setFile(null);
        setFileUrl(null);
        setError(null);
        setUploading(false);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm animate-fadeIn p-4">
            <div className="bg-white rounded-3xl w-full max-w-4xl overflow-hidden shadow-2xl flex flex-col max-h-[90vh]">
                <div className="p-6 border-b flex justify-between items-center bg-gray-50">
                    <h3 className="font-serif text-xl font-bold">{modalTitle}</h3>
                    <button onClick={handleClose} className="text-gray-400 hover:text-black">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-8">
                    {error && (
                        <div className="bg-red-50 text-red-600 p-4 rounded-xl mb-6 text-sm font-bold">
                            {error}
                        </div>
                    )}

                    {!fileUrl ? (
                        <div className="border-2 border-dashed border-gray-300 rounded-3xl p-20 text-center hover:border-vanity-pink transition-all group cursor-pointer relative">
                            <input
                                type="file"
                                onChange={onFileChange}
                                accept="image/*"
                                className="absolute inset-0 opacity-0 cursor-pointer"
                            />
                            <div className="space-y-4">
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto group-hover:bg-vanity-pink group-hover:text-white transition-colors">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                                    </svg>
                                </div>
                                <div className="font-black uppercase tracking-widest text-gray-400 group-hover:text-vanity-pink">Select Image to Upload</div>
                                <div className="text-xs text-gray-400">Supports JPG, PNG, WEBP</div>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            <div className="relative h-[50vh] bg-black rounded-2xl overflow-hidden shadow-inner">
                                <Cropper
                                    image={fileUrl}
                                    crop={crop}
                                    zoom={zoom}
                                    aspect={aspect}
                                    onCropChange={setCrop}
                                    onCropComplete={onCropComplete}
                                    onZoomChange={setZoom}
                                />
                            </div>
                            <div className="flex items-center gap-4">
                                <span className="text-xs font-black uppercase tracking-widest text-gray-400 w-16">Ratio</span>
                                <div className="flex gap-2">
                                    <button onClick={() => setAspect(16 / 9)} className={`px-3 py-1 text-xs rounded border ${aspect === 16 / 9 ? 'bg-black text-white' : 'bg-white text-gray-600'}`}>16:9</button>
                                    <button onClick={() => setAspect(4 / 3)} className={`px-3 py-1 text-xs rounded border ${aspect === 4 / 3 ? 'bg-black text-white' : 'bg-white text-gray-600'}`}>4:3</button>
                                    <button onClick={() => setAspect(3 / 4)} className={`px-3 py-1 text-xs rounded border ${aspect === 3 / 4 ? 'bg-black text-white' : 'bg-white text-gray-600'}`}>3:4</button>
                                    <button onClick={() => setAspect(1 / 1)} className={`px-3 py-1 text-xs rounded border ${aspect === 1 / 1 ? 'bg-black text-white' : 'bg-white text-gray-600'}`}>1:1</button>
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <span className="text-xs font-black uppercase tracking-widest text-gray-400 w-16">Zoom</span>
                                <input
                                    type="range"
                                    value={zoom}
                                    min={1}
                                    max={3}
                                    step={0.1}
                                    aria-labelledby="Zoom"
                                    onChange={(e) => setZoom(Number(e.target.value))}
                                    className="flex-1 accent-black h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                />
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-6 bg-gray-50 border-t flex justify-end gap-4">
                    <button
                        onClick={handleClose}
                        className="px-8 py-4 rounded-xl text-xs font-black uppercase tracking-widest text-gray-500 hover:text-black hover:bg-gray-100 transition-all"
                        disabled={uploading}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleUpload}
                        disabled={!file || uploading}
                        className="bg-black text-white px-10 py-4 rounded-xl text-xs font-black uppercase tracking-widest shadow-lg hover:bg-vanity-pink disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                    >
                        {uploading ? (
                            <>
                                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Processing...
                            </>
                        ) : (
                            'Confirm & Upload'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default StandardImageUploader;
